/** Formats milliseconds as h:mm:ss or m:ss. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}

/**
 * The same reading as `formatDuration`, split before the seconds.
 *
 * The play page sets the seconds in a quieter colour: they tick twice a second
 * and are not what a solver tracks, so at display size an undivided readout
 * flickers in peripheral vision. Splitting here rather than in the component
 * keeps the two formats from drifting apart.
 */
export function splitDuration(ms: number): { lead: string; seconds: string } {
  const formatted = formatDuration(ms);
  const cut = formatted.lastIndexOf(":");
  return { lead: formatted.slice(0, cut + 1), seconds: formatted.slice(cut + 1) };
}

/**
 * Client clock offset so the cosmetic timer follows the server's `revealed_at`.
 * Returns elapsed milliseconds computed from server timestamps; the server recomputes anyway.
 */
export function elapsedSince(
  revealedAt: string,
  serverNow: string,
  receivedAt: number,
  now: number,
): number {
  const offset = new Date(serverNow).getTime() - receivedAt;
  return now + offset - new Date(revealedAt).getTime();
}

/**
 * "2 minutes ago", in the reader's language, from an ISO timestamp. Coarsens with distance:
 * seconds under a minute, then minutes, hours and days. Anything older than a month is
 * given as a plain date, since "34 days ago" is not how anyone reads a feed.
 */
export function formatRelativeTime(iso: string, now: number, locale: string): string {
  const delta = new Date(iso).getTime() - now;
  const abs = Math.abs(delta);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (abs < 60_000) return format.format(Math.round(delta / 1000), "second");
  if (abs < 3_600_000) return format.format(Math.round(delta / 60_000), "minute");
  if (abs < 86_400_000) return format.format(Math.round(delta / 3_600_000), "hour");
  if (abs < 30 * 86_400_000) return format.format(Math.round(delta / 86_400_000), "day");
  return new Date(iso).toLocaleDateString(locale);
}
